
import React, { useCallback, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Student } from "@/services/db";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { toast } from 'sonner';

// Import our new component parts
import StudentInfo from "./card/StudentInfo";
import ProgramsList from "./card/ProgramsList";
import CardActions from "./card/CardActions";

interface StudentCardProps {
  student: Student;
  onDelete: (id: number) => Promise<void>;
}

const StudentCard = ({ student, onDelete }: StudentCardProps) => {
  const navigate = useNavigate();

  const handleViewDetails = useCallback(() => {
    console.log(`Navigating to student details for ID: ${student.id}`);
    navigate(`/students/${student.id}`);
  }, [student.id, navigate]);

  const handleEdit = useCallback(() => {
    console.log(`Navigating to edit student for ID: ${student.id}`);
    navigate(`/student-form/${student.id}`);
  }, [student.id, navigate]);

  const handleDelete = useCallback(async () => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${student.name}? This action cannot be undone.`
    );
    
    if (confirmDelete) {
      try {
        console.log(`Deleting student: ${student.name} (ID: ${student.id})`);
        await onDelete(student.id);
        toast.success(`${student.name} has been deleted successfully`);
      } catch (error) {
        console.error('Error deleting student:', error);
        toast.error('Failed to delete student. Please try again.');
      }
    }
  }, [student.id, student.name, onDelete]);

  const handleCardClick = useCallback((event: React.MouseEvent) => {
    // Prevent navigation if clicking on action buttons
    if ((event.target as HTMLElement).closest('[data-action-button]')) {
      return;
    }
    
    console.log(`Card clicked for student: ${student.name} (ID: ${student.id})`);
    handleViewDetails();
  }, [handleViewDetails, student.name, student.id]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleViewDetails();
    }
  }, [handleViewDetails]);

  // Ensure student has valid data
  if (!student || !student.id) {
    console.warn('Invalid student data received:', student);
    return null;
  }

  return (
    <Card 
      className="hover:shadow-lg transition-all duration-200 cursor-pointer transform hover:scale-[1.02] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${student.name}`}
    >
      <CardContent className="p-3">
        <StudentInfo student={student} />
        <ProgramsList programs={student.programs || []} />
      </CardContent>
      
      <CardFooter className="px-3 pb-3 pt-0">
        <CardActions 
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewDetails={handleViewDetails}
          studentName={student.name}
        />
      </CardFooter>
    </Card>
  );
};

export default StudentCard;
